export function groupBy<T>(array: T[], fn: (t: T) => string): Partial<Record<string, T[]>> {
  return array.reduce<Record<string, T[]>>((acc, t) => {
    const key = fn(t)
    if (!acc[key]) acc[key] = []
    acc[key].push(t)
    return acc
  }, {})
}

export function entries<T extends object>(obj: T): { [K in keyof T]: [K, T[K]] }[keyof T][] {
  return Object.entries(obj) as { [K in keyof T]: [K, T[K]] }[keyof T][]
}

export function keys<T extends object>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[]
}

export function values<T extends object>(obj: T): T[keyof T][] {
  return Object.values(obj) as T[keyof T][]
}

export function objectForEach<T extends object>(
  obj: T,
  fn: (value: T[keyof T], key: keyof T, object: T) => void
): void {
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      fn(obj[key], key, obj)
    }
  }
}

export function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined)) as Partial<T>
}

export function stripUndefinedDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripUndefinedDeep)
  if (value === null || typeof value !== 'object') return value
  if (Object.getPrototypeOf(value) !== Object.prototype) return value

  return Object.fromEntries(
    Object.entries(value)
      .filter(([, item]) => item !== undefined)
      .map(([key, item]) => [key, stripUndefinedDeep(item)])
  )
}

export function isObject(item: unknown): item is Record<string, unknown> {
  return item !== null && typeof item === 'object'
}
